import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Button, Icon, IconProps, Popup, SemanticICONS } from 'semantic-ui-react';

interface AnimatedArrowProps {
  direction?: 'up' | 'down' | 'left' | 'right';
  size?: number;
  className?: string;
  iconClassName: SemanticICONS;
  handleClick: () => void;
  disableGlowAnimation?: boolean
  asSubmit?: boolean
  hoverText?: string
}

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const GlowingEffect = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(59, 130, 246, 0.6);
  border-radius: 9999px;
  filter: blur(10px);
  animation: ${pulse} 1.5s infinite;
  opacity: 0.6;
  z-index: 1;
`;

const PulsingRing = styled.div`
  position: absolute;
  inset: 0;
  border: 2px solid #3b82f6;
  border-radius: 9999px;
  animation: ${pulse} 1.5s infinite;
  opacity: 0.75;
  z-index: 0;
`;

const ArrowWrapper = styled.div<{
  rotate: string;
  isHighlighted: boolean;
  size: number,
  disableGlowAnimation: boolean
 }>`
  position: relative;
  display: inline-block;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  color: ${({ isHighlighted, disableGlowAnimation }) => (isHighlighted && !disableGlowAnimation ? '#3b82f6' : '#4b5563')};
  z-index: 2;
  transition: all 0.3s ease;
  ${({ isHighlighted, disableGlowAnimation }) => isHighlighted && !disableGlowAnimation && css`
    animation: ${bounce} 1s infinite;
    transform: scale(1.1);
    filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.8));
    i {
      margin-top: 5px !important;
    }
  `}
  transform: ${({ rotate }) => rotate};

  i {
    margin-top: 3px !important;
  }

  button {
    background-color: transparent !important;
    font-size: 1.2rem !important;
    padding: 0px !important;
    min-height: 2em !important;
    &:hover {
      background-color: transparent !important;
      background: transparent !important;
    }
  }
`;

const getRotation = (direction: string) => {
  switch (direction) {
    case 'up':
      return 'rotate(-90deg)';
    case 'down':
      return 'rotate(90deg)';
    case 'left':
      return 'rotate(180deg)';
    default:
      return 'rotate(0deg)';
  }
};

type Props = AnimatedArrowProps & IconProps

const AnimatedArrow: React.FC<Props> = ({
  direction = 'right',
  size = 40,
  className = '',
  iconClassName,
  handleClick,
  disableGlowAnimation,
  asSubmit,
  hoverText
}) => {
  const [isHighlighted, setIsHighlighted] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsHighlighted(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  const rotate = getRotation(direction);

  function renderSubmitArrow() {
    return <Button icon type={'submit'} style={{ background: 'transparent' }}>
      {hoverText ? <Popup
        content={hoverText}
        trigger={ <Icon
          name={iconClassName}
          size='large'
        />}
      /> : <Icon
        name={iconClassName}
        size='large'
      />}
    </Button>
  }

  function renderArrow() {
    return hoverText ? <Popup
      content={hoverText}
      trigger={ <Icon
        name={iconClassName}
        size='large'
        onClick={() => handleClick()}
      />}
    /> : <Icon
      name={iconClassName}
      size='large'
      onClick={() => handleClick()}
    />
  }

  return (
    <div className={`relative ${className}`} style={{ position: 'relative', display: 'inline-block' }}>
      {isHighlighted && !disableGlowAnimation && <GlowingEffect />}
      <ArrowWrapper
        rotate={rotate}
        isHighlighted={isHighlighted}
        size={size}
        disableGlowAnimation={disableGlowAnimation}
      >
        {asSubmit ? renderSubmitArrow() : renderArrow()}
       
      </ArrowWrapper>
      {isHighlighted && !disableGlowAnimation && <PulsingRing />}
    </div>
  );
};

export default AnimatedArrow;
